<template>
  <v-card color="primary" class="ui-module-card">
    <v-card-title class="justify-space-between align-center">
      <div class="text-h6 font-weight-bold">Evaluaciones anuales</div>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <div v-if="!evaluaciones || evaluaciones.length === 0" class="text-center text-subtitle-1">
        No hay evaluaciones anuales registradas.
      </div>
      <v-data-table
        v-else
        :headers="headers"
        :items="sortedEvaluaciones"
        item-key="id"
        class="transparent"
        hide-default-footer
        :search="search"
      >
        <template v-slot:top>
          <v-row class="mb-2">
            <v-col cols="4">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Buscar"
                dense
                filled
                rounded
                background-color="transparent"
                color="secondary"
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
        </template>
        <template v-slot:[`item.fecha`]="{ item }">
          <span>{{ item.fecha }}</span>
        </template>
        <template v-slot:[`item.tipo`]="{ item }">
          <span>{{ item.fk_idCatTipoEvaluacion == 6 ? 'Funciones Administrativas' : 'Funciones Operativas' }}</span>
        </template>
        <template v-slot:[`item.estatus`]="{ item }">
          <template v-if="!estatusEvaluacionOptions || estatusEvaluacionOptions.length === 0">
            <v-progress-circular indeterminate color="secondary" size="20" />
          </template>
          <template v-else-if="isStatusLoading(item)">
            <v-progress-circular indeterminate color="secondary" size="20" />
          </template>
          <EvaluacionStatusChip
            v-else
            :status="item.CatEstatusEvaluacion ? item.CatEstatusEvaluacion.title : 'Sin estatus'"
            :statusOptions="estatusEvaluacionOptions"
            :editable="true"
            @update-status="onUpdateEstatus(item, $event)"
          />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="secondary"
                v-bind="attrs"
                v-on="on"
                :loading="isEditLoading(item)"
                :disabled="isEditLoading(item)"
                @click.stop="openEditDialog(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Editar evaluación</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="secondary"
                v-bind="attrs"
                v-on="on"
                :loading="loading"
                :disabled="loading"
                @click.stop="generateExcel(item)"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Descargar evaluación</span>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="text-center py-4">Sin datos</div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>
<script>
import ExportEvaluacionAnual from '@/modules/evaluacion-anual/domain/entities/ExportEvaluacionAnual.js'
import EditarEvaluacionAnualFormComponent from './EditarEvaluacionAnualFormComponent.vue'
import EvaluacionStatusChip from '@/components/desarrollo_policial/evaluacion-desempeño/chips/EvaluacionStatusChip.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'EvaluacionesAnualesTable',
  components: { EvaluacionStatusChip },
  mounted() {
    this.$store.dispatch('estatusEvaluacion/fetchEstatusEvaluacion')
  },
  props: {
    evaluaciones: {
      type: Array,
      default: () => [],
    },
    idPersonal: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: 'Fecha', value: 'fecha' },
        { text: 'Tipo', value: 'tipo' },
        { text: 'Estatus', value: 'estatus' },
        { text: 'Opciones', value: 'actions', sortable: false },
      ],
      search: '',
      loading: false,
      editLoadingId: null,
      statusLoadingId: null,
      buffer: null,
    }
  },
  computed: {
    ...mapGetters('estatusEvaluacion', { estatusEvaluacionOptions: 'getEstatusEvaluacion' }),

    sortedEvaluaciones() {
      // Ordenar por fecha (formato esperado DD/MM/YYYY) descendente
      const parseFecha = (f) => {
        if (!f || typeof f !== 'string') return 0
        const parts = f.split('/')
        if (parts.length === 3) {
          const [dd, mm, yyyy] = parts
          return new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd)).getTime()
        }
        // Fallback para otros formatos reconocibles por Date.parse
        const t = Date.parse(f)
        return isNaN(t) ? 0 : t
      }
      return [...this.evaluaciones].sort((a, b) => parseFecha(b.fecha) - parseFecha(a.fecha))
    },
  },
  methods: {
    ...mapMutations('dialog', ['SET_DIALOG']),
    ...mapActions('personal', ['fetchEvaluadores']),
    ...mapActions('evaluacionAnual', ['updateEstatus', 'fetchEvaluacionAnual']),

    isStatusLoading(item) {
      return !!this.statusLoadingId && this.statusLoadingId === item.id
    },

    async onUpdateEstatus(item, nuevoEstatus) {
      this.statusLoadingId = item.id
      try {
        await this.updateEstatus({
          fk_idPersonal: this.idPersonal,
          id: item.id,
          fk_idCatEstatusEvaluacion: nuevoEstatus.id,
        })
        await this.fetchEvaluacionAnual(this.idPersonal)
      } finally {
        this.statusLoadingId = null
      }
    },

    isEditLoading(item) {
      return !!this.editLoadingId && this.editLoadingId === (item.idEvaluacionAnual || item.id)
    },

    async openEditDialog(evaluacion) {
      this.editLoadingId = evaluacion.idEvaluacionAnual || evaluacion.id
      try {
        await this.fetchEvaluadores(evaluacion.PersonalEvaluador.CUIP)
        this.SET_DIALOG({
          type: 'EDITAR_EVALUACION_ANUAL',
          title: 'Editar evaluación anual',
          component: EditarEvaluacionAnualFormComponent,
          width: '90vw',
          props: {
            evaluacion,
            idPersonal: this.idPersonal,
          },
        })
      } finally {
        this.editLoadingId = null
      }
    },

    async generateExcel(evaluacion) {
      this.loading = true
      try {
        const exporter = new ExportEvaluacionAnual(evaluacion, this.getFullName)
        await exporter.generateXlsx()
      } catch (e) {
        console.log('Error al generar excel evaluación anual', e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
::v-deep .v-input__slot {
  border-radius: 12px !important;
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.25) !important;
  background: rgba(16, 16, 30, 0.35) !important;
  box-shadow: none !important;
  transition: all 0.3s ease !important;
}

::v-deep .v-input__slot:hover {
  border-color: rgba(var(--v-secondary-base-rgb), 0.45) !important;
}

::v-deep .v-input--is-focused .v-input__slot {
  border-color: rgba(var(--v-secondary-base-rgb), 0.7) !important;
  background: rgba(16, 16, 30, 0.5) !important;
}

::v-deep .v-input__control {
  background: transparent !important;
  border-radius: 12px;
  border: none !important;
  box-shadow: none !important;
}

::v-deep input {
  color: #e0e0e0 !important;
}

::v-deep .v-label,
::v-deep .v-input .v-icon {
  color: rgba(var(--v-secondary-base-rgb), 0.75) !important;
}

::v-deep .v-input--is-focused .v-label {
  color: var(--v-secondary-base) !important;
}

::v-deep .v-data-table {
  background: transparent !important;
  color: #e0e0e0 !important;
  border-radius: 12px;
}

::v-deep .v-data-table-header {
  background: rgba(var(--v-secondary-base-rgb), 0.15) !important;
}

::v-deep .v-data-table-header th {
  color: var(--v-secondary-base) !important;
  font-weight: 700 !important;
  font-size: 0.875rem !important;
  border-bottom: 2px solid rgba(var(--v-secondary-base-rgb), 0.3) !important;
  padding: 12px 16px !important;
}

::v-deep .v-data-table tbody tr:hover {
  background: rgba(var(--v-secondary-base-rgb), 0.1) !important;
}

::v-deep .v-data-table tbody td {
  color: #e0e0e0 !important;
  border-bottom: 1px solid rgba(var(--v-secondary-base-rgb), 0.1) !important;
}

::v-deep .v-divider {
  border-color: rgba(var(--v-secondary-base-rgb), 0.15) !important;
}

::v-deep .v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  position: relative;
  overflow: hidden;
}

::v-deep .v-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
  pointer-events: none;
}

::v-deep .v-btn:active::before {
  width: 300px;
  height: 300px;
}

::v-deep .v-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(var(--v-secondary-base-rgb), 0.6) !important;
}

.text-h6 {
  color: #ffffff !important;
}
</style>
